import { useCallback } from 'react';
import { useRouter } from 'next/router';
import { trackEvent } from '@kcd/event-tracker';

import VirtualizedList from '~/components/VirtualizedList';
import { useCurrentUser } from '~/domains/auth/queries';
import ProductDisplayTotalCount from '~/domains/products/components/ProductDisplayTotalCount';
import EmptyShowcase from '~/pages/offers/EmptyShowcase';
import type { OAISchema } from '~/types/openapi';
import { chunk } from '~/utils/array';
import { personalData } from '~/utils/personalData';

import { useRegisterMembership } from '../../hooks';
import { useCashnotePlusShowcase } from '../../queries';
import PlusMembershipCard from '../PlusMembershipCard';

import styles from './index.module.css';

type Props = {
  code: string;
  viewCount?: boolean;
};

const PlusmembershipDisplay = ({ code, viewCount }: Props) => {
  const { pages, isFetchingNextPage, fetchNextPage } =
    useCashnotePlusShowcase(code);
  const registerMembership = useRegisterMembership();

  const count = pages?.length || 0;
  const { user } = useCurrentUser();
  const router = useRouter();

  const onItemClick = useCallback(
    (item: OAISchema['MarketSelectProductResponseV2']) => {
      trackEvent('clickded 캐시노트마켓 product/플러스 멤버십 전용관', {
        user: personalData(user),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        showCashnotePlusSuggestion: item?.showCashnotePlusSuggestion || false,
        productName: item.productName,
        productId: item.offerId,
      });

      if (item?.showCashnotePlusSuggestion) {
        void registerMembership();
        return;
      }

      void router.push(`/vendors/${item.vendorId}/products/${item.offerId!}`);
    },
    [registerMembership, router, user],
  );

  if (count === 0) {
    return <EmptyShowcase />;
  }

  const chunked = chunk(pages || [], 2);
  return (
    <article>
      {viewCount && <ProductDisplayTotalCount totalCount={count || 0} />}
      <div className={styles.displayContainer}>
        <VirtualizedList
          data={chunked}
          isFetchingNextPage={isFetchingNextPage}
          endReached={fetchNextPage}
          // SSR시에 초기 height를 잡기 위해서 item height가 dynamic할 수 있기 때문에 defaultItemHeight 사용
          defaultItemHeight={246}
          // SSR시에 서버에서 몇개까지 그릴지 결정 (https://github.com/petyosi/react-virtuoso/issues/341)
          initialItemCount={
            chunked.length > 1 ? chunked.length - 1 : chunked.length
          }
          itemContent={(rowIndex, rowData) => (
            <>
              <div className={styles.container}>
                {rowData.map((item) => {
                  return (
                    <PlusMembershipCard
                      key={item.offerId}
                      item={item}
                      onItemClick={() => onItemClick(item)}
                    />
                  );
                })}
              </div>
              {rowIndex < chunked.length - 1 && <div className="h-24" />}
            </>
          )}
        />
      </div>
    </article>
  );
};

export default PlusmembershipDisplay;
